import { Controller } from "@hotwired/stimulus";
import { uuidv4 } from "/utils/uuid";
import { isEscPressed } from "/utils/shortcuts";

type RemoveListenerFn = () => void;

let removeListeners: RemoveListenerFn = () => {};

export function togglePopoverHandler(button: HTMLElement, panel: HTMLElement): void {
  if (panel.classList.contains("hidden")) {
    openPopover(panel);
  } else {
    closePopover(panel);
  }
}

function openPopover(panel: HTMLElement): void {
  panel.classList.add("block");
  panel.classList.remove("hidden");
  panel.focus();
  hide(panel, panel);
}

function closePopover(panel: HTMLElement): void {
  panel.classList.remove("block");
  panel.classList.add("hidden");
  removeListeners();
}

function hide(element: HTMLElement, panel: HTMLElement): void {
  const outsideClickListener = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    event.preventDefault();
    if (
      event.target.classList.contains("popover-close") ||
      event.target.parentElement.classList.contains("popover-close") ||
      (!event.target.classList.contains("day-item") &&
        !event.target.classList.contains("button-previous-month") &&
        !event.target.classList.contains("button-next-month") &&
        !element.contains(event.target) &&
        isVisible(element) &&
        !event.code)
    ) {
      closePopover(panel);
    }
  };

  const escKeydownListener = (event: KeyboardEvent): void => {
    if (isVisible(element) && isEscPressed(event)) {
      closePopover(panel);
    }
  };

  const removeClickListener: RemoveListenerFn = () => {
    document.removeEventListener("click", outsideClickListener);
  };

  const removeEscKeydownListener: RemoveListenerFn = () => {
    document.removeEventListener("keydown", escKeydownListener);
  };

  removeListeners = () => {
    removeClickListener();
    removeEscKeydownListener();
  };

  setTimeout(() => {
    document.addEventListener("click", outsideClickListener);
    document.addEventListener("keydown", escKeydownListener);
  }, 0);
}

function isVisible(elem: HTMLElement): boolean {
  return !!elem && !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
}

export default class PopoverController extends Controller {
  declare element: HTMLElement;
  private readonly elementId: string;

  initialize(): void {
    this.elementId = this.element.id || `popover-${uuidv4()}`;
  }

  connect(): void {
    if (!this.element.hasAttribute("id")) {
      this.element.setAttribute("id", this.elementId);
    }
    this.update();
  }

  update(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>(`#${this.elementId} > button`);
    const panels = document.querySelectorAll<HTMLElement>(`#${this.elementId} .popover-panel`);

    buttons.forEach((button, i) => {
      const panel = panels[i];
      if (!panel) return;

      if (!button.id) {
        const buttonId = `${this.elementId}Button${i}`;
        button.setAttribute("id", buttonId);
        panel.setAttribute("aria-labelledby", buttonId);
      } else {
        panel.setAttribute("aria-labelledby", button.id);
      }

      const panelId = `${this.elementId}Panel${i}`;
      button.setAttribute("aria-controls", panelId);
      button.setAttribute("aria-haspopup", "true");
      button.setAttribute("aria-expanded", "false");

      if (!panel.hasAttribute("id")) {
        panel.setAttribute("id", panelId);
      }

      button.addEventListener("click", () => {
        togglePopoverHandler(button, panel);
      });
    });
  }
}
